import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ReactTable from "src/components/tables/react-table/ReactTable";
import { getFormattedDate, handleCsvExport } from "src/helpers/helpers";
import useDebounce from "src/hooks/useDebounce";
import useManualRefresh from "src/hooks/useManualRefresh";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { useDeleteStaff, useGetStaff } from "../api-clients";
import { StaffType } from "./staff-types";
import useStaffTableColumns from "./useStaffTableColumns";

const StaffList = () => {
  useSetActiveTab(TAB_NAMES.STAFF_MANAGEMENT);
  const navigate = useNavigate();
  const [length, setLength] = useState(10);
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText);
  const { successNotification, errorNotification } = useNotification();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToOperate, setIdToOperate] = useState<number | undefined>(undefined);
  const {
    isLoading,
    data,
    mutate: updateStaffData,
  } = useGetStaff({ length, page, search: debouncedSearch });

  const { isRefreshing, handleRefresh } = useManualRefresh(updateStaffData);

  const {
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSucceed,
    error: deleteErr,
    execute: deleteStaff,
  } = useDeleteStaff();

  const tableData = useMemo(() => data?.data?.rows ?? [], [data?.data]);

  const mappedTableData = useMemo(() => {
    return tableData.map((staff: StaffType) => {
      return {
        ...staff,
        doj: staff.doj ? getFormattedDate(staff.doj) : "",
      };
    });
  }, [tableData]);

  const totalRecords = useMemo(() => data?.data?.count, [data?.data?.count]);

  const columns = useStaffTableColumns({
    onDelete: (id: number) => {
      setIdToOperate(id);
      setIsDeleteModalOpen(true);
    },
  });

  useEffect(() => {
    if (isDeleteSucceed) {
      updateStaffData();
      setIsDeleteModalOpen(false);
      successNotification("Staff Deleted.");
    }
  }, [isDeleteSucceed, successNotification, updateStaffData]);

  useEffect(() => {
    if (deleteErr) {
      errorNotification();
    }
  }, [deleteErr, errorNotification]);

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteStaff(idToOperate)}
      />
      <div className="flex flex-col gap-[20px]">
        <TabHeader
          label="Staff Management"
          onRefresh={() => handleRefresh()}
          exportButton={{
            label: "Export",
            onClick: () => handleCsvExport(mappedTableData),
          }}
          printButton={{
            label: "Print",
            onClick: () => window.print(),
          }}
          createButton={{
            label: "Add Staff",
            onClick: () => navigate("create"),
          }}
          search={{
            onChange: (val) => setSearchText(val),
            value: searchText,
          }}
        />
        <ReactTable
          columns={columns}
          data={mappedTableData}
          isLoading={isLoading || isRefreshing}
          pagination={{
            setLength,
            length,
            total: totalRecords,
            page,
            setPage,
          }}
        />
      </div>
    </>
  );
};

export default StaffList;
